import React, { createContext, FC, ReactNode, useContext, useMemo } from "react";
import { i18n } from "i18n";
import { IconTypesKeys } from "@evergis/ui";

import { mergeObjects } from "../../../utils/mergeObjects";
import { ToolsHintBlockType } from "../../ToolsHint/types";
import { useDialogReset } from "../../../hooks/useDialogs";
import { Dialog } from "./styled";

export type DialogContextType = {
  title?: {
    /** заголовок модального окна */
    text?: string | JSX.Element;
    /** иконка модального окна (рядом с заголовком) */
    icon: IconTypesKeys | null;
  };
  subTitle?: string | JSX.Element;
  meta?: {
    aliasLabel: string;
    descriptionLabel: string;
    loaderLabel: string;
    copyrightLabel?: string;
    systemNameLabel?: string;
  };
  applyText?: string;
  attributes?: {
    label?: string;
    description?: string;
    icon?: IconTypesKeys | null;
    addAttribute?: string;
  };
  help?: {
    /** открыта ли подсказка или нет */
    openAtFirst?: boolean;
    hintsBlock: ToolsHintBlockType;
  };
};

const getDefaultDialogContext = (): DialogContextType => {
  return {
    title: {
      text: "",
      icon: null,
    },
    meta: {
      aliasLabel: "",
      descriptionLabel: "",
      loaderLabel: "",
      copyrightLabel: i18n.t("copyright.copyright", { ns: "common" }),
      systemNameLabel: i18n.t("systemName", { ns: "common" }),
    },
    applyText: "",
    attributes: {
      label: "",
      description: "",
      icon: null,
      addAttribute: i18n.t("addAttribute", { ns: "common" }),
    },
  };
};

export interface DefaultFullScreenDialogProps {
  className?: string;
  children?: ReactNode;
  dialogContext?: DialogContextType;
  onApply?: VoidFunction;
}

const DialogContext = createContext<DialogContextType>(getDefaultDialogContext());

export const DialogContextProvider = ({ dialogContext, children }) => {
  const memoContext = useMemo(() => mergeObjects(getDefaultDialogContext(), dialogContext), [dialogContext]);

  return <DialogContext.Provider value={memoContext}>{children}</DialogContext.Provider>;
};

export const useDialogContext = (): DialogContextType => useContext(DialogContext);

export const FullScreenDialog: FC<DefaultFullScreenDialogProps> = ({ children, dialogContext, onApply, className }) => {
  const cancelDialog = useDialogReset();

  return (
    <Dialog isOpen onCloseRequest={cancelDialog} modal fullScreen onApply={onApply} className={className}>
      <DialogContextProvider dialogContext={dialogContext}>{children}</DialogContextProvider>
    </Dialog>
  );
};
